import type { TypesenseProductDocument } from "@/features/instantsearch/types/instantsearch-types";
import {
  PRODUCT_COLLECTION,
  getCatalogLang,
  queryByFields,
} from "@/features/instantsearch/utils/collection-utils";
import { getMultiSearchResults } from "@/features/instantsearch/utils/multi-search";

export interface PreferenceBrand {
  id: string; // brand.id if available, else brand name
  name: string; // brand.name
  count: number; // approximate (from facet)
}

const TYPESENSE_CONFIG = {
  apiUrl: `${import.meta.env.VITE_TYPESENSE_API_URL}multi_search`,
  authHeader: "X-TYPESENSE-API-KEY",
  apiKey: import.meta.env.VITE_TYPESENSE_API_KEY as string,
};

function escapeTypesenseValue(value: string): string {
  return value.includes(" ") || value.includes(",") || value.includes(":")
    ? `\`${value}\``
    : value;
}

// Typesense filter_by max ops = 100; for OR chain n values => ~ (2n - 1)
const MAX_OR_VALUES_PER_FILTER = 50;

function normalizeBrand(raw: any): { id: string | null; name: string | null } {
  if (!raw) return { id: null, name: null };
  const id = raw.id ?? raw.brandId ?? null;
  const name = raw.name ?? raw.brandName ?? null;
  return {
    id: id === null || id === undefined ? null : String(id),
    name: name === null || name === undefined ? null : String(name),
  };
}

/**
 * Typesense üzerinden tüm brand'leri facet olarak üretir.
 * - facet: brand.name
 * - ardından küçük bir doc havuzuyla brand.name -> brand.id mapping yapılır (mümkünse)
 */
export async function fetchPreferenceBrands(params?: {
  limit?: number; // max facet values to consider
  samplePool?: number; // doc sample size per batch (max 250)
  language?: string; // kullanıcının aktif dili (UI store adı) — merged koleksiyonda alan son ekini belirler
  region?: string; // kullanıcının aktif bölgesi — countries filtrelemesi için
}): Promise<PreferenceBrand[]> {
  const limit = Math.min(Math.max(params?.limit ?? 200, 1), 500);
  const samplePool = Math.min(Math.max(params?.samplePool ?? 250, 1), 250);
  const collection = PRODUCT_COLLECTION;
  const queryBy = queryByFields(
    getCatalogLang(params?.language),
    "preferenceBrands",
  );
  const region = params?.region?.trim() || "";

  // 1) facet brand names
  const facetRequest = {
    collection,
    q: "*",
    query_by: queryBy,
    page: 1,
    per_page: 0,
    facet_by: "brand.name",
    max_facet_values: limit,
    exhaustive_search: false,
    highlight_fields: "none",
    exclude_fields: "search_time_ms,out_of",
    use_cache: false,
    cache_ttl: 60,
    ...(region ? { filter_by: `countries:${region}` } : {}),
  };

  const facetResponse = await fetch(TYPESENSE_CONFIG.apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      [TYPESENSE_CONFIG.authHeader]: TYPESENSE_CONFIG.apiKey,
    },
    body: JSON.stringify({ searches: [facetRequest] }),
  });

  if (!facetResponse.ok) {
    throw new Error(
      `Typesense API Error: ${facetResponse.status} - ${facetResponse.statusText}`,
    );
  }

  const facetData = await facetResponse.json();
  const facetResult = getMultiSearchResults(facetData)[0] || {};
  const facetCounts = facetResult.facet_counts || [];

  const brandFacet = facetCounts.find(
    (f: any) => f.field_name === "brand.name",
  );
  const counts: Array<{ value: string; count: number }> =
    brandFacet?.counts || [];

  const topBrandNames = counts
    .map((c) => ({
      name: String(c.value || "").trim(),
      count: Number(c.count || 0),
    }))
    .filter((b) => !!b.name)
    .slice(0, limit);

  if (topBrandNames.length === 0) return [];

  // 2) map brand name -> brand id by sampling docs (batched to avoid max ops)
  const chunks: Array<Array<{ name: string; count: number }>> = [];
  for (let i = 0; i < topBrandNames.length; i += MAX_OR_VALUES_PER_FILTER) {
    chunks.push(topBrandNames.slice(i, i + MAX_OR_VALUES_PER_FILTER));
  }

  const docRequests = chunks.map((chunk) => {
    const orFilter = chunk
      .map((b) => `brand.name:=${escapeTypesenseValue(b.name)}`)
      .join(" || ");
    return {
      collection,
      q: "*",
      query_by: queryBy,
      page: 1,
      per_page: samplePool,
      filter_by: region
        ? `countries:${region} && (${orFilter})`
        : `(${orFilter})`,
      sort_by: "effectivePrice:desc",
      include_fields: "id,brand",
      exhaustive_search: false,
      highlight_fields: "none",
      exclude_fields: "search_time_ms,out_of",
      use_cache: false,
      cache_ttl: 60,
    };
  });

  const docsResponse = await fetch(TYPESENSE_CONFIG.apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      [TYPESENSE_CONFIG.authHeader]: TYPESENSE_CONFIG.apiKey,
    },
    body: JSON.stringify({ searches: docRequests }),
  });

  if (!docsResponse.ok) {
    throw new Error(
      `Typesense API Error: ${docsResponse.status} - ${docsResponse.statusText}`,
    );
  }

  const docsData = await docsResponse.json();
  const docs = getMultiSearchResults(docsData)
    .flatMap((r: any) => (r?.hits || []).map((h: any) => h.document))
    .filter(Boolean) as TypesenseProductDocument[];

  const nameToId = new Map<string, string>();
  for (const d of docs) {
    const b = normalizeBrand((d as any)?.brand);
    if (!b.name) continue;
    if (b.id && !nameToId.has(b.name)) {
      nameToId.set(b.name, b.id);
    }
  }

  return topBrandNames.map((b) => ({
    id: nameToId.get(b.name) || b.name, // fallback to name as stable key
    name: b.name,
    count: b.count,
  }));
}
